import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import { BETA_FEATURES } from './betaFeatures';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

/** Who to take off the list: by email reaches every sign-in of the person, by sub only the rows named. */
export type BetaRemoval = { email: string } | { subs: string[] };

export type BetaAccessActions = {
  add: (email: string) => Promise<void>;
  remove: (who: BetaRemoval) => Promise<void>;
};

export class BetaAccessError extends Error {
  constructor(readonly reason: string) {
    super(`beta access change failed: ${reason}`);
  }
}

async function send(method: 'PUT' | 'DELETE', body: Record<string, string>): Promise<void> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/admin/analytics/beta-access`, {
    method,
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new BetaAccessError(data?.error ?? String(response.status));
  }
}

/** A beta user is in every beta feature, and the list stores one grant per feature. */
export async function addToBeta(email: string): Promise<void> {
  await Promise.all(BETA_FEATURES.map((feature) => send('PUT', { email, feature: feature.id })));
}

export async function removeFromBeta(who: BetaRemoval): Promise<void> {
  const targets = 'email' in who ? [{ email: who.email }] : who.subs.map((sub) => ({ sub }));
  await Promise.all(targets.flatMap((target) => BETA_FEATURES.map((feature) => send('DELETE', { ...target, feature: feature.id }))));
}

export const BETA_ACCESS_ACTIONS: BetaAccessActions = { add: addToBeta, remove: removeFromBeta };
